import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Sparkles, Folder, Calendar, Phone, ArrowRight } from "lucide-react";
import { BOT_KNOWLEDGE } from "../../utils/botKnowledge";
import { processUserQuery, handleLeadStep, INITIAL_LEAD_STATE } from "../../utils/botEngine";
import { useContactModal } from "../../context/ContactContext";
import aiMsgLogo from "../../assets/NewHome/OurAi/Ai-msg.png";
import "./ChatBot.css";

const renderPromptIcon = (intent) => {
  switch (intent) {
    case "pricing":
      return <Tag size={13} style={{ marginRight: "5px" }} />;
    case "services":
      return <Sparkles size={13} style={{ marginRight: "5px" }} />;
    case "portfolio":
      return <Folder size={13} style={{ marginRight: "5px" }} />;
    case "lead_start":
      return <Calendar size={13} style={{ marginRight: "5px" }} />;
    case "contact":
      return <Phone size={13} style={{ marginRight: "5px" }} />;
    default:
      return null;
  }
};

const ChatBot = () => {
  const navigate = useNavigate();
  const { openContactModal } = useContactModal();

  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(1);
  const [leadState, setLeadState] = useState(INITIAL_LEAD_STATE);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [messages, setMessages] = useState([
    {
      id: "welcome-1",
      sender: "bot",
      text: "Welcome to **Metatrybe**!\n\nI can answer questions about our **services, pricing plans, timeline, tech stack**, or help you request a **custom project quote**.",
      buttons: BOT_KNOWLEDGE.quickPrompts,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
  ]);

  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Toggle chat window open/close
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  // Process sending a message (user text or button action)
  const sendMessage = async (textToSend = null, intent = null, actionNavigate = null, actionValue = null) => {
    const messageText = textToSend || inputValue.trim();
    if (!messageText && !intent && !actionNavigate && !actionValue) return;

    if (actionNavigate) {
      navigate(actionNavigate);
      setIsOpen(false);
      return;
    }

    const timeStr = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    // Append user message if user typed or clicked
    const userMsgObj = {
      id: Date.now() + "-user",
      sender: "user",
      text: messageText || (intent ? intent.toUpperCase() : "Selected Action"),
      timestamp: timeStr
    };

    setMessages((prev) => [...prev, userMsgObj]);
    setInputValue("");
    setIsTyping(true);

    // Simulated short response delay for natural conversational feel
    setTimeout(async () => {
      let botResponse;

      if (leadState.active) {
        // Continue lead capture flow
        const valueToPass = actionValue || messageText;
        botResponse = await handleLeadStep(valueToPass, leadState, setLeadState);
      } else {
        // Process regular intent query
        const queryToProcess = intent || messageText;
        botResponse = processUserQuery(queryToProcess, leadState, setLeadState);
      }

      const botMsgObj = {
        id: Date.now() + "-bot",
        sender: "bot",
        text: botResponse.text,
        buttons: botResponse.buttons || [],
        timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      };

      setIsTyping(false);
      setMessages((prev) => [...prev, botMsgObj]);

      // If response requires navigation
      if (botResponse.actionNavigate) {
        setTimeout(() => {
          navigate(botResponse.actionNavigate);
          setIsOpen(false);
        }, 1200);
      }

      // If response requires opening lead contact modal
      if (botResponse.actionModal) {
        setTimeout(() => {
          openContactModal();
          setIsOpen(false);
        }, 1200);
      }
    }, 450);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Helper to render bold markdown text (**text**)
  const renderFormattedText = (text) => {
    if (!text) return "";
    const lines = text.split("\n");
    return lines.map((line, lIdx) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      const renderedLine = parts.map((part, pIdx) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("*") && part.endsWith("*")) {
          return <em key={pIdx}>{part.slice(1, -1)}</em>;
        }
        return part;
      });
      return (
        <React.Fragment key={lIdx}>
          {renderedLine}
          {lIdx < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="metatrybe-chatbot-wrapper">
      {/* Floating Launcher Button */}
      <button
        type="button"
        className={`chatbot-launcher-btn ${isOpen ? "active" : ""}`}
        onClick={toggleChat}
        aria-label="Open AI Support Chat"
      >
        <span className="chatbot-launcher-pulse" />
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <div className="chatbot-launcher-icon">
            <img src={aiMsgLogo} alt="ChatBot AI" className="chatbot-launcher-img" />
            {unreadCount > 0 && <span className="chatbot-unread-badge">{unreadCount}</span>}
          </div>
        )}
      </button>

      {/* Chat Window Modal */}
      {isOpen && (
        <div
          className="chatbot-container"
          data-lenis-prevent="true"
          data-lenis-prevent-wheel="true"
          data-lenis-prevent-touch="true"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar-circle">
                <img src={aiMsgLogo} alt="Metatrybe AI" className="chatbot-avatar-img" />
                <span className="chatbot-online-dot" />
              </div>
              <div className="chatbot-header-text">
                <h3 className="chatbot-title">Metatrybe AI</h3>
                <p className="chatbot-subtitle">Website & Service Assistant</p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button
                type="button"
                className="chatbot-action-icon-btn"
                onClick={toggleChat}
                aria-label="Close Chat"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div className="chatbot-messages-feed">
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-msg-row ${msg.sender}`}>
                {msg.sender === "bot" && (
                  <div className="chatbot-msg-avatar">
                    <img src={aiMsgLogo} alt="AI" className="chatbot-msg-avatar-img" />
                  </div>
                )}
                <div className="chatbot-msg-bubble-container">
                  <div className="chatbot-msg-bubble">
                    {renderFormattedText(msg.text)}
                  </div>
                  {msg.buttons && msg.buttons.length > 0 && (
                    <div className="chatbot-msg-buttons-grid">
                      {msg.buttons.map((btn, bIdx) => (
                        <button
                          key={bIdx}
                          type="button"
                          className="chatbot-prompt-btn"
                          onClick={() =>
                            sendMessage(
                              btn.label,
                              btn.intent,
                              btn.actionNavigate,
                              btn.actionValue
                            )
                          }
                        >
                          {renderPromptIcon(btn.intent)}
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <span className="chatbot-msg-timestamp">{msg.timestamp}</span>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chatbot-msg-row bot">
                <div className="chatbot-msg-avatar">
                  <img src={aiMsgLogo} alt="AI" className="chatbot-msg-avatar-img" />
                </div>
                <div className="chatbot-msg-bubble typing">
                  <span className="chatbot-typing-dot"></span>
                  <span className="chatbot-typing-dot"></span>
                  <span className="chatbot-typing-dot"></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Lead Progress Banner if active */}
          {leadState.active && (
            <div className="chatbot-lead-banner">
              <span>Step {leadState.step + 1} of 4: Project Quote</span>
              <button
                type="button"
                className="chatbot-lead-cancel"
                onClick={() => sendMessage("cancel")}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Input Bar */}
          <div className="chatbot-input-container">
            <input
              type="text"
              className="chatbot-input-field"
              placeholder={
                leadState.active
                  ? "Type your answer..."
                  : "Ask about pricing, services, quote..."
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              className="chatbot-send-btn"
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isTyping}
              aria-label="Send Message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
